'use client';
import React, { useState, useMemo } from 'react';
import Image from 'next/image';
import {
  Content,
  ImageFieldImage,
  KeyTextField,
  LinkField,
  RichTextField,
  asText,
  isFilled,
} from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import {
  SliceComponentProps,
  PrismicRichText,
  JSXMapSerializer,
} from '@prismicio/react';

interface Client {
  logo: ImageFieldImage | null | undefined;
  case_study_link?: LinkField | null | undefined;
  brief_description?: RichTextField | null | undefined;
  button_label?: KeyTextField;
}

interface ClientCaseStudyButtonProps {
  isValidLink: boolean;
  button_label?: KeyTextField;
  case_study_link?: LinkField;
}

/**
 * Props for `ClientsList`.
 */
export type ClientsListProps = SliceComponentProps<Content.ClientsListSlice>;

type WithTitleSubtitleDescriptioSlice = Extract<
  Content.ClientsListSlice,
  { variation: 'withTitleSubtitleDescriptio' }
>;

const highlightedTextComponents: JSXMapSerializer = {
  paragraph: ({ children }) => (
    <p className="text-[#00f557] text-center text-[18px] md:text-[22px] font-medium leading-[1.4]">
      {children}
    </p>
  ),
};

const titleComponents: JSXMapSerializer = {
  heading1: ({ children }) => (
    <h1 className="text-white text-center text-[32px] md:text-[42px] font-semibold leading-[1.2] capitalize w-full">
      {children}
    </h1>
  ),
  heading2: ({ children }) => (
    <h2 className="text-white text-center text-[32px] md:text-[42px] font-semibold leading-[1.2] capitalize w-full">
      {children}
    </h2>
  ),
  paragraph: ({ children }) => (
    <p className="text-white text-center text-[32px] md:text-[42px] font-semibold leading-[1.2] capitalize w-full">
      {children}
    </p>
  ),
};

const descriptionComponents: JSXMapSerializer = {
  paragraph: ({ children }) => (
    <p className="text-white text-center text-[16px] font-light leading-[1.4]">
      {children}
    </p>
  ),
};

const ClientsListWithTitle = ({
  slice,
}: {
  slice: WithTitleSubtitleDescriptioSlice;
}) => {
  const {
    title,
    pill_label,
    highlighted_text,
    description,
    clients,
    background_left,
    background_right,
  } = slice.primary;

  const gridCols = 3;
  const gridRows = Math.ceil(clients.length / gridCols);
  const cardGradient = (col: number, row: number) => {
    const isPerimeter =
      col === 0 || col === gridCols - 1 || row === 0 || row === gridRows - 1;
    if (!isPerimeter) {
      return 'rgba(19,9,74,0.8)';
    }
    const isCorner =
      (col === 0 || col === gridCols - 1) &&
      (row === 0 || row === gridRows - 1);
    if (isCorner) {
      const cx = col === 0 ? '100%' : '0%';
      const cy = row === 0 ? '100%' : '0%';
      return `radial-gradient(ellipse 85% 40% at ${cx} ${cy}, rgba(19,9,74,0.8) 0%, rgba(4,17,24,0.64) 100%)`;
    }
    const nx = gridCols > 1 ? col / (gridCols - 1) : 0.5;
    const ny = gridRows > 1 ? row / (gridRows - 1) : 0.5;
    const dx = nx - 0.5;
    const dy = ny - 0.5;
    const angle = ((Math.atan2(-dx, dy) * 180) / Math.PI + 360) % 360;
    return `linear-gradient(${angle.toFixed(2)}deg, rgba(4,17,24,0.64) 49.5%, rgba(19,9,74,0.8) 65.5%)`;
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative overflow-hidden rounded-[24px] bg-[#041118] mx-5 md:mx-16 my-8 px-6 md:px-[60px] py-[60px] md:py-[80px]"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none rounded-[24px]"
      >
        <PrismicNextImage
          field={background_left}
          fill
          fallbackAlt=""
          className="object-cover opacity-20 rounded-[24px]"
        />
        {background_right.url && (
          <div className="absolute inset-0 opacity-20 overflow-hidden rounded-[24px]">
            <div
              className="absolute top-0 h-full max-w-none"
              style={{ left: '58%', width: '147%' }}
            >
              <Image
                src={background_right.url}
                alt=""
                fill
                sizes="147vw"
                className="object-fill"
              />
            </div>
          </div>
        )}
      </div>

      <div className="relative z-10 flex flex-col items-center gap-[30px] max-w-[1260px] mx-auto">
        {pill_label && (
          <div className="border border-[#00f557] px-4 py-2 rounded-full shrink-0">
            <span className="text-white text-[14px] font-normal leading-[1.2] uppercase tracking-wide">
              {pill_label}
            </span>
          </div>
        )}

        <div className="max-w-[1080px] w-full">
          <PrismicRichText field={title} components={titleComponents} />
        </div>

        <div className="max-w-[1080px] w-full">
          <PrismicRichText
            field={highlighted_text}
            components={highlightedTextComponents}
          />
        </div>

        <div className="max-w-[1080px] w-full">
          <PrismicRichText
            field={description}
            components={descriptionComponents}
          />
        </div>

        <div className="w-full overflow-hidden rounded-tl-[16px] rounded-tr-[16px] grid grid-cols-2 md:grid-cols-3 border-l border-t border-[#a887ff]/40">
          {clients.map((client, i) => (
            <div
              key={i}
              className="h-[110px] flex items-center justify-center border-r border-b border-[#a887ff]/40 px-6"
              style={{
                background: cardGradient(
                  i % gridCols,
                  Math.floor(i / gridCols),
                ),
              }}
            >
              <PrismicNextImage
                field={client.logo}
                className="max-h-[60px] w-auto object-contain"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ClientCard = ({
  logo,
  case_study_link,
  brief_description,
  button_label,
}: Client) => {
  const [isHovered, setIsHovered] = useState(false);
  const isValidLink = case_study_link
    ? Object.keys(case_study_link).length > 1
    : false;

  const CardContent = (
    <div
      className="group h-[170px] bg-white-gradient rounded-2xl flex justify-center items-center relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <PrismicNextImage field={logo} className="mx-4" />

      <ClientCaseStudyButton
        isValidLink={isValidLink}
        button_label={button_label}
        case_study_link={case_study_link ?? undefined}
      />

      {brief_description && brief_description?.length > 0 && (
        <div
          className={`w-full p-12 absolute top-4 left-0 rounded-2xl bg-white-gradient backdrop-blur-sm shadow-xl transition-all duration-150 transform ${
            isHovered
              ? 'opacity-100 translate-y-0 pointer-events-auto'
              : 'opacity-0 -translate-y-6 pointer-events-none'
          } z-10`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="min-h-full w-full mx-4 text-primary-dark-grey">
            <PrismicNextImage field={logo} className="mb-8" />
            <PrismicRichText field={brief_description}></PrismicRichText>
            <ClientCaseStudyButton
              isValidLink={isValidLink}
              button_label={button_label}
              case_study_link={case_study_link ?? undefined}
            />
          </div>
        </div>
      )}
    </div>
  );

  return isValidLink ? (
    <PrismicNextLink field={case_study_link}>{CardContent}</PrismicNextLink>
  ) : (
    <>{CardContent}</>
  );
};

const ClientCaseStudyButton = ({
  isValidLink,
  button_label,
  case_study_link,
}: ClientCaseStudyButtonProps) => {
  return (
    <>
      {isValidLink && button_label && (
        <PrismicNextLink
          field={case_study_link}
          className="button-outline-gray flex justify-center items-center absolute right-2 bottom-1 pointer-events-auto"
        >
          <span>{button_label}</span>
        </PrismicNextLink>
      )}
    </>
  );
};

type ThemedTitleSubtitleDescriptionSlice = Extract<
  Content.ClientsListSlice,
  { variation: 'themedTitleSubtitleDescription' }
>;

const themedDescriptionComponents = (isDark: boolean): JSXMapSerializer => ({
  paragraph: ({ children }) => (
    <p
      className={`text-[16px] font-light leading-[1.5] md:text-[18px] [&:not(:first-child)]:mt-4 ${
        isDark ? 'text-white' : 'text-[#041118]'
      }`}
    >
      {children}
    </p>
  ),
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
});

const ClientsListThemed = ({
  slice,
}: {
  slice: ThemedTitleSubtitleDescriptionSlice;
}) => {
  const { theme, title, subtitle, description, clients } = slice.primary;
  const isDark = theme !== 'light';
  const hasTitle = isFilled.richText(title);
  const hasSubtitle = isFilled.richText(subtitle);
  const hasDescription = isFilled.richText(description);

  const sectionClass = isDark
    ? 'bg-[#262626] text-white'
    : 'bg-white text-[#041118]';
  const titleClass = isDark ? 'text-white' : 'text-[#041118]';
  const subtitleClass = isDark ? 'text-[#05f273]' : 'text-[#00bc96]';

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`relative overflow-hidden px-5 py-16 md:px-[30px] md:py-20 lg:py-[70px] ${sectionClass}`}
    >
      <div className="relative mx-auto w-full max-w-[1280px] flex flex-col gap-9">
        {(hasTitle || hasSubtitle) && (
          <h2 className="text-balance break-words text-[28px] font-medium leading-[1.2] md:text-[42px] w-full">
            {hasTitle && <span className={titleClass}>{asText(title)}</span>}
            {hasTitle && hasSubtitle && <br />}
            {hasSubtitle && (
              <span className={subtitleClass}>{asText(subtitle)}</span>
            )}
          </h2>
        )}

        {hasDescription && (
          <div className="w-full">
            <PrismicRichText
              field={description}
              components={themedDescriptionComponents(isDark)}
            />
          </div>
        )}

        {clients.length > 0 && (
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {clients.map((client, i) => (
              <div
                key={i}
                className="bg-[#F3F3F3] rounded-lg h-[137px] flex items-center justify-center px-6 py-4"
              >
                <PrismicNextImage
                  field={client.logo}
                  className="max-h-[75%] max-w-[75%] w-auto h-auto object-contain"
                  fallbackAlt=""
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

type DefaultSlice = Extract<Content.ClientsListSlice, { variation: 'default' }>;

const ClientsListDefault = ({ slice }: { slice: DefaultSlice }) => {
  const {
    primary: { title, button_label_view_study_case, clients },
  } = slice;

  const [visibleRows, setVisibleRows] = useState(2);

  const clientsGrid = useMemo(() => {
    const firstAlternateRow = 3;
    const secondAlternateRow = 2;
    const finalRowCount = 4;
    let rowAlternateGrid = firstAlternateRow;
    const grid: Client[][] = [];

    for (let i = 0; i < clients.length; ) {
      const remaining = clients.length - i;

      if (remaining > finalRowCount) {
        const rowSize = Math.min(rowAlternateGrid, remaining);
        grid.push(clients.slice(i, i + rowSize) as Client[]);
        i += rowSize;

        rowAlternateGrid =
          rowSize === secondAlternateRow
            ? firstAlternateRow
            : secondAlternateRow;
      } else {
        grid.push(clients.slice(i, i + remaining) as Client[]);
        break;
      }
    }

    return grid;
  }, [clients]);

  const handleViewMore = () => {
    setVisibleRows((prev) => prev + 2);
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="text-white mx-auto px-5 md:px-16 pb-28 max-w-screen-2xl"
    >
      <div className="flex justify-center">
        <PrismicRichText field={title} />
      </div>

      <div className="mx-6 md:mx-0 mt-12">
        <div className="hidden md:block">
          {clientsGrid.map((row, rowIndex) => {
            let numberOfCols = 'md:grid-cols-3';

            if (row.length === 2) {
              numberOfCols = 'md:grid-cols-2';
            }

            if (row.length === 4) {
              numberOfCols = 'md:grid-cols-4';
            }

            return (
              <div
                key={rowIndex}
                className={`grid gap-2 mt-2 grid-cols-1 ${numberOfCols}`}
              >
                {row.map((client, colIndex) => (
                  <ClientCard
                    key={colIndex}
                    {...client}
                    button_label={button_label_view_study_case}
                  />
                ))}
              </div>
            );
          })}
        </div>

        <div className="block md:hidden">
          {clientsGrid.slice(0, visibleRows).map((row, rowIndex) => (
            <div key={rowIndex} className="grid gap-2 mt-2 grid-cols-1">
              {row.map((client, colIndex) => (
                <ClientCard
                  key={colIndex}
                  {...client}
                  button_label={button_label_view_study_case}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="block md:hidden">
        {visibleRows < clientsGrid.length && (
          <div className="flex justify-center mt-8">
            <button
              onClick={handleViewMore}
              className="button-bordered"
              aria-label="View more clients"
            >
              View More
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * Component for "ClientsList" Slices.
 */
const ClientsList = ({ slice }: ClientsListProps): JSX.Element => {
  if (slice.variation === 'withTitleSubtitleDescriptio') {
    return <ClientsListWithTitle slice={slice} />;
  }
  if (slice.variation === 'themedTitleSubtitleDescription') {
    return <ClientsListThemed slice={slice} />;
  }
  return <ClientsListDefault slice={slice} />;
};

export default ClientsList;
